import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { BannerInterface } from 'src/app/interfaces/lobby.interface';
import { GameTileService } from 'src/app/services/game-tile/game-tile.service';
import { ControlService } from 'src/app/services/control/control.service';

@Component({
  selector: 'mobile-carousel',
  templateUrl: './mobile-carousel.component.html',
  styleUrls: ['./mobile-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileCarousel implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('itemList', { static: false }) itemList!: ElementRef<HTMLElement>;
  @ViewChildren('carouselElement', { read: ElementRef }) carouselElement!: QueryList<ElementRef>;

  @Input() items: any[]     = [];
  @Input() autoSlide       = false;
  @Input() slideInterval   = 3000;
  @Output() slideClicked   = new EventEmitter<BannerInterface>();

  public showLeftButton  = false;
  public showRightButton = false;
  public disableScroll   = false;

  private autoSlideTimer: any;
  private currentSlide = 0;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private control: ControlService,
    private platform: Platform,
    private service: GameTileService,
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    const container = this.itemList.nativeElement;

   
    container.addEventListener('scroll', () => this.updateCurrentSlide());

    if (this.autoSlide) {
      this.autoSlideTimer = setInterval(() => this.autoScroll(), this.slideInterval);
    }
  }

  ngOnDestroy(): void {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
    }
    this.itemList.nativeElement.removeEventListener('scroll', () => this.updateCurrentSlide());
  }

  private updateCurrentSlide(): void {
    const scrollLeft = this.itemList.nativeElement.scrollLeft;
    let closestIndex = 0;
    let minDiff = Infinity;

    this.carouselElement.forEach((elRef, idx) => {
      const offset = elRef.nativeElement.offsetLeft;
      const diff = Math.abs(offset - scrollLeft);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = idx;
      }
    });

    this.currentSlide = closestIndex;
  }

  private autoScroll(): void {
    const container = this.itemList.nativeElement;
    const slides = this.carouselElement.toArray();

    this.currentSlide = (this.currentSlide + 1) % slides.length;
    const targetEl = slides[this.currentSlide].nativeElement as HTMLElement;

    this.animateScroll(container, targetEl.offsetLeft, 600);

    this.updateArrowVisibility();
    this.changeDetectorRef.markForCheck();
  }

  private animateScroll(
    container: HTMLElement,
    to: number,
    duration: number
  ): void {
    const start = container.scrollLeft;
    const change = to - start;
    let startTime: number | null = null;

   
    const easeInOutQuad = (t: number) =>
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const step = (timestamp: number) => {
      startTime = startTime ?? timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      container.scrollLeft = start + change * easeInOutQuad(progress);
      if (elapsed < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  public scrollGamesRight(): void {
    const container = this.itemList.nativeElement;
    const pages = this.calculateScrollElements();
    this.currentSlide = Math.min(this.currentSlide + pages, this.items.length - 1);
    const targetEl = this.carouselElement.toArray()[this.currentSlide].nativeElement as HTMLElement;
    this.animateScroll(container, targetEl.offsetLeft, 400);
  }

  public scrollGamesLeft(): void {
    const container = this.itemList.nativeElement;
    const pages = this.calculateScrollElements();
    this.currentSlide = Math.max(this.currentSlide - pages, 0);
    const targetEl = this.carouselElement.toArray()[this.currentSlide].nativeElement as HTMLElement;
    this.animateScroll(container, targetEl.offsetLeft, 400);
  }

  private updateArrowVisibility(): void {
    const container = this.itemList.nativeElement;
    this.showLeftButton  = container.scrollLeft > 0;
    this.showRightButton = container.scrollLeft < container.scrollWidth - container.clientWidth;
  }

  public onSlideClick(index: number): void {
    this.slideClicked.emit(this.items[index]);
  }

  public reachesLeft($event: boolean): void {
    this.showLeftButton = !$event;
    this.changeDetectorRef.detectChanges();
  }

  public reachesRight($event: boolean): void {
    this.showRightButton = !$event;
    this.changeDetectorRef.detectChanges();
  }

  private calculateScrollElements(): number {
    const screenW  = this.itemList.nativeElement.clientWidth;
    const elementW = this.carouselElement.first.nativeElement.getBoundingClientRect().width;
    return Math.floor(screenW / elementW);
  }

  public trackById (index: number, item: any): number {
    return item.id || index;  
  }
}
